/**
 * Highlights a nav link whose target section is currently in view.
 *
 * Scans the nav's links, pairs each `#hash` link with its target element, then
 * on scroll marks the first (top-most) visible target's link with the given
 * active class. Uses the same rAF-throttled scroll loop regardless of caller.
 *
 * @param {Element} nav_el  Container to scan for links.
 * @param {Object}  options
 * @param {string}  options.link_selector Selector for the links. Default `a[href^="#"]`.
 * @param {string}  options.active_class  Class toggled on the active link. Default `is-active`.
 * @param {string}  options.sticky         Selector for sticky elements; their bottom edge is the
 *                                         active line, so a clicked target isn't beaten by the
 *                                         section peeking out behind a sticky header.
 * @param {Function} options.on_change     Called with the active element (or null) on change.
 * @returns {{ update: Function, destroy: Function }|null}
 */
export function create_scroll_spy(nav_el, options = {}) {
	const { link_selector = `a[href^="#"]`, active_class = `is-active`, sticky, on_change } = options;

	const link_els = nav_el.querySelectorAll(link_selector);

	const links = [];

	for (const link_el of link_els) {
		const href = link_el.getAttribute(`href`);

		if (!href || href.charAt(0) !== `#`) {
			continue;
		}

		const target_el = document.getElementById(href.slice(1));

		if (!target_el) {
			continue;
		}

		links.push({
			link_el,
			target_el,
		});
	}

	if (links.length === 0) {
		return null;
	}

	let ticking = false;

	let active_el = null;

	function get_reference() {
		if (!sticky) {
			return 0;
		}

		let reference = 0;

		for (const el of document.querySelectorAll(sticky)) {
			reference = Math.max(reference, el.getBoundingClientRect().bottom);
		}

		return reference;
	}

	function update() {
		const reference = get_reference() + 1;

		let best_top = -Infinity;

		let next_el = null;

		for (const link of links) {
			const top = link.target_el.getBoundingClientRect().top;

			if (top > reference || top <= best_top) {
				continue;
			}

			best_top = top;
			next_el = link.link_el;
		}

		if (next_el === active_el) {
			ticking = false;

			return;
		}

		if (active_el) {
			active_el.classList.remove(active_class);
		}

		if (next_el) {
			next_el.classList.add(active_class);
		}

		active_el = next_el;

		if (on_change) {
			on_change(active_el);
		}

		ticking = false;
	}

	function request_tick() {
		if (ticking) {
			return;
		}

		window.requestAnimationFrame(update);

		ticking = true;
	}

	window.addEventListener(`scroll`, request_tick);
	window.addEventListener(`resize`, request_tick);

	update();

	return {
		update,
		destroy() {
			window.removeEventListener(`scroll`, request_tick);
			window.removeEventListener(`resize`, request_tick);
		},
	};
}
